import { matchRoutes, NavigationType, type RouteObject } from 'react-router-dom';
import { BillRouteTemplateSchema, type UiUsageEventPayload } from '@packages/types';

type PageView = UiUsageEventPayload<'ui-usage.page.viewed'>;

/**
 * The route template the pathname matched, or `not-found` for the catch-all. Null when the
 * match is not in the registry (nothing to record rather than a raw path). Path parameters
 * and query strings never reach the result because only the route's own `path` is read.
 */
export function pageViewRoute(routes: RouteObject[], pathname: string): PageView['route'] | null {
  const matches = matchRoutes(routes, pathname);
  const path = matches?.[matches.length - 1]?.route.path;
  if (path === undefined || path === '*') return 'not-found';
  const template = BillRouteTemplateSchema.safeParse(path);
  return template.success ? template.data : null;
}

/**
 * The `navigation` field of a page view: how the user arrived on the template.
 *
 * - `initial`: the first view recorded since collection started. That is the page load
 *   (reloads included), or the moment the account arrives, whichever is later. It is
 *   the template the user actually landed on, so a redirect such as `/` to `/dashboard`
 *   that finished before collection started is never recorded separately.
 * - `push`, `replace`, `pop`: every later view, from the router's navigation type (a link or
 *   `navigate()`, a redirect or `navigate(..., { replace })`, and Back/Forward).
 */
export function pageViewNavigation(isFirstView: boolean, navigationType: NavigationType): PageView['navigation'] {
  if (isFirstView) return 'initial';
  if (navigationType === NavigationType.Push) return 'push';
  if (navigationType === NavigationType.Replace) return 'replace';
  return 'pop';
}
